import { useEffect, useMemo, useState } from "react";
import { http, currency, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Check, CreditCard, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import { http as api2 } from "@/lib/api";

const StatusBadge = ({ s }) => {
  const map = {
    paid: "bg-[hsl(var(--success)/0.15)] text-[hsl(var(--success))] border-[hsl(var(--success)/0.4)]",
    pending: "bg-[hsl(var(--warning)/0.15)] text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.4)]",
    overdue: "bg-[hsl(var(--destructive)/0.15)] text-[hsl(var(--destructive))] border-[hsl(var(--destructive)/0.4)]",
  };
  return <span className={`inline-block border font-mono text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-sm ${map[s]}`}>{s}</span>;
};

export default function Billing() {
  const [inv, setInv] = useState([]);
  const [subs, setSubs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subscriber_id: "", amount: 799, due_date: new Date(Date.now() + 15 * 86400000).toISOString().slice(0, 10), notes: "" });

  const load = async () => {
    const [a, b] = await Promise.all([http.get("/invoices"), http.get("/subscribers")]);
    setInv(a.data); setSubs(b.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? inv : inv.filter((i) => i.status === filter);

  const totals = useMemo(() => {
    const paid = inv.filter((i) => i.status === "paid").reduce((s, i) => s + i.amount, 0);
    const pending = inv.filter((i) => i.status === "pending").reduce((s, i) => s + i.amount, 0);
    const overdue = inv.filter((i) => i.status === "overdue").reduce((s, i) => s + i.amount, 0);
    return { paid, pending, overdue };
  }, [inv]);

  const create = async (e) => {
    e.preventDefault();
    await http.post("/invoices", { ...form, amount: Number(form.amount) });
    toast.success("Invoice created");
    setOpen(false);
    load();
  };

  const pay = async (id) => {
    await http.post(`/invoices/${id}/pay`);
    toast.success("Invoice marked paid");
    load();
  };

  const payOnline = async (id) => {
    try {
      const { data } = await http.post("/payments/invoice-checkout", {
        invoice_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to start payment");
    }
  };

  const downloadPdf = async (inv) => {
    try {
      const { data } = await http.get(`/invoices/${inv.id}/download.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.invoice_no}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download PDF"); }
  };

  const subMap = Object.fromEntries(subs.map((s) => [s.id, s]));

  return (
    <div className="space-y-6 fade-up" data-testid="billing-view">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="uppercase-label">Recurring billing</div>
          <h1 className="font-display font-bold text-4xl mt-1">Invoices & payments</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-invoice-btn" className="rounded-sm bg-primary hover:bg-primary/90"><Plus size={14} className="mr-2" weight="bold" /> New invoice</Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader><DialogTitle className="font-display">Create invoice</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div>
                <Label className="uppercase-label">Subscriber</Label>
                <Select value={form.subscriber_id} onValueChange={(v) => setForm({ ...form, subscriber_id: v })}>
                  <SelectTrigger data-testid="inv-subscriber" className="mt-1.5"><SelectValue placeholder="Select subscriber" /></SelectTrigger>
                  <SelectContent className="max-h-72">{subs.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name} · {s.username}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="uppercase-label">Amount (INR)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5" required data-testid="inv-amount" /></div>
                <div><Label className="uppercase-label">Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1.5" required data-testid="inv-due" /></div>
              </div>
              <div><Label className="uppercase-label">Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>
              <DialogFooter><Button type="submit" data-testid="inv-submit" className="rounded-sm">Create</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Total label="Collected" value={currency(totals.paid)} tone="success" />
        <Total label="Pending" value={currency(totals.pending)} tone="warning" />
        <Total label="Overdue" value={currency(totals.overdue)} tone="destructive" />
      </div>

      <div className="flex items-center gap-2">
        {["all", "paid", "pending", "overdue"].map((k) => (
          <button
            key={k}
            data-testid={`billing-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`text-xs uppercase tracking-wide px-3 py-1.5 border rounded-sm transition-colors ${filter === k ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"}`}
          >
            {k}
          </button>
        ))}
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border">
            <th className="uppercase-label text-left py-2 px-3">Invoice</th>
            <th className="uppercase-label text-left py-2 px-3">Subscriber</th>
            <th className="uppercase-label text-right py-2 px-3">Amount</th>
            <th className="uppercase-label text-left py-2 px-3">Due</th>
            <th className="uppercase-label text-left py-2 px-3">Status</th>
            <th className="uppercase-label text-right py-2 px-3">Action</th>
          </tr></thead>
          <tbody>
            {filtered.map((i) => (
              <tr key={i.id} className="border-b border-border/60 hover:bg-accent/30" data-testid={`invoice-row-${i.id}`}>
                <td className="py-2 px-3 data-cell">{i.invoice_no}</td>
                <td className="py-2 px-3">{subMap[i.subscriber_id]?.full_name || "—"}</td>
                <td className="py-2 px-3 data-cell text-right">{currency(i.amount)}</td>
                <td className="py-2 px-3 data-cell text-muted-foreground">{i.due_date}</td>
                <td className="py-2 px-3"><StatusBadge s={i.status} /></td>
                <td className="py-2 px-3 text-right">
                  {i.status !== "paid" && (
                    <div className="inline-flex items-center gap-3">
                      <button onClick={() => payOnline(i.id)} className="text-primary hover:underline text-xs font-mono inline-flex items-center gap-1" data-testid={`pay-online-${i.id}`}>
                        <CreditCard size={12} weight="bold" /> pay online
                      </button>
                      <button onClick={() => pay(i.id)} className="text-muted-foreground hover:text-foreground hover:underline text-xs font-mono inline-flex items-center gap-1" data-testid={`pay-${i.id}`}>
                        <Check size={12} weight="bold" /> mark paid
                      </button>
                      <button onClick={() => downloadPdf(i)} className="text-muted-foreground hover:text-foreground hover:underline text-xs font-mono inline-flex items-center gap-1" data-testid={`pdf-${i.id}`}>
                        <FilePdf size={12} weight="bold" /> pdf
                      </button>
                    </div>
                  )}
                  {i.status === "paid" && (
                    <div className="inline-flex items-center gap-3">
                      <span className="text-xs text-muted-foreground font-mono">{fmtDate(i.paid_at)}</span>
                      <button onClick={() => downloadPdf(i)} className="text-primary hover:underline text-xs font-mono inline-flex items-center gap-1" data-testid={`pdf-${i.id}`}>
                        <FilePdf size={12} weight="bold" /> pdf
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-muted-foreground text-sm">No invoices.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Total = ({ label, value, tone }) => {
  const color = tone === "success" ? "text-[hsl(var(--success))]" : tone === "warning" ? "text-[hsl(var(--warning))]" : "text-[hsl(var(--destructive))]";
  return (
    <div className="border border-border bg-card p-5">
      <div className="uppercase-label">{label}</div>
      <div className={`font-display text-3xl font-bold mt-2 ${color}`}>{value}</div>
    </div>
  );
};
